import { FC, useContext, useState } from 'react';
import CustomButton, { ButtonType } from 'components/custom-button/CustomButton';
import { EditTableIcon, ExportIcon, NavCloseIcon } from 'assets/icons';
import { useAppDispatch, useAppSelector } from 'hooks';
import {
  getNotifications,
  selectCurrentTeam,
  selectCurrentTeamAccountingPermissions,
  showNotifier
} from 'containers/app/appSlice';
import { PayRunMemberJob } from '../confirmJob.model';
import { selectSelectedReviewTab } from '../confirmJobsSlice';
import { JobReviewStatus, PayRunStatus } from '../../constants';
import { getJobReviewStatusMap } from '../../utils';
import { NotifierTypes } from 'constants/notifierConstants';
import { InfoMesssages } from 'types/infoMessages';
import { TooltipWrapper } from 'components/tooltip/TooltipWrapper';
import { PayRunContext } from '../../MemberReview';
import MoveJobToPayRunModal from '../../components/MoveJobToPayRunModal';
import { useParams } from 'react-router-dom';
import { If } from 'components';
import { useMoveBulkJobsToPayRunMutation } from '../../api';

interface JobReviewTableActionsProps {
  selectedJobs: PayRunMemberJob[];
  onClearSelection: () => void;
  onExportClick: () => void;
  onEditTableClick: () => void;
  onShowConfirmModal: (status: string) => void;
  hasDraftAdjustment: boolean;
}

const JobReviewTableActions: FC<JobReviewTableActionsProps> = ({
  selectedJobs,
  onClearSelection,
  onExportClick,
  onEditTableClick,
  onShowConfirmModal,
  hasDraftAdjustment
}) => {
  const { approveAndProcessAccounting } = useAppSelector(selectCurrentTeamAccountingPermissions) ?? {};
  const selectedReviewTab = useAppSelector(selectSelectedReviewTab);
  const notifications = useAppSelector(getNotifications);
  const dispatch = useAppDispatch();
  const { payRunStatus } = useContext(PayRunContext);
  const { payRunId } = useParams();
  const [moveBulkJobsToPayRun, { isLoading: isMoveBulkJobToPayRunLoading }] = useMoveBulkJobsToPayRunMutation();
  const currentTeam = useAppSelector(selectCurrentTeam);

  const [isMoveJobToPayRunModalOpen, setIsMoveJobToPayRunModalOpen] = useState(false);

  const handleStatusSelect = (status: string) => {
    if (!selectedJobs.length || !approveAndProcessAccounting) return;

    if (hasDraftAdjustment && status === JobReviewStatus.CONFIRMED) {
      const notification = notifications.find(n => n.id === 'job-review-actions');
      if (!notification) {
        dispatch(
          showNotifier({
            id: 'job-review-actions',
            type: NotifierTypes.WARNING,
            message: { primaryMessage: 'You cannot confirm the jobs because some have pending adjustments.' },
            showClose: true
          })
        );
      }
      return;
    }
    onShowConfirmModal(status);
  };

  const handleMoveToOpenPayRun = () => {
    setIsMoveJobToPayRunModalOpen(true);
  };

  const handleMove = (selectedPayRun: string) => {
    moveBulkJobsToPayRun({
      teamId: currentTeam?.id,
      sourcePayRunId: payRunId,
      targetPayRunId: selectedPayRun,
      jobIds: selectedJobs.map(job => job.jobId)
    })
      .unwrap()
      .then(() => {
        setIsMoveJobToPayRunModalOpen(false);
      });
  };

  const getStatusOptions = () => {
    const relevantStatuses = [
      JobReviewStatus.NEEDS_REVIEW,
      JobReviewStatus.IN_PROGRESS,
      JobReviewStatus.CONFIRMED,
      JobReviewStatus.SKIPPED
    ];

    return relevantStatuses
      .filter(status => status !== (selectedReviewTab as unknown as JobReviewStatus))
      .map(status => ({
        value: status,
        label: status === JobReviewStatus.SKIPPED ? 'skipped' : getJobReviewStatusMap(status).label?.toLowerCase()
      }));
  };

  return (
    <>
      <div
        id="job-review-table-actions"
        className="flex h-10 w-full items-center border border-borderGray bg-[#F4F4F4] py-1 pl-[30px] pr-[39px]">
        {selectedJobs.length > 0 ? (
          <div className="flex w-full items-center justify-between gap-4">
            <div className="flex items-center justify-start gap-4">
              <span className="text-sbase font-semibold text-accentText">
                {selectedJobs.length} {selectedJobs.length === 1 ? 'job' : 'jobs'} selected
              </span>
              <div className="flex items-center gap-6">
                {getStatusOptions().map(option => (
                  <CustomButton
                    key={option.value}
                    id={`mark-as-${option.value}`}
                    type={ButtonType.GHOST}
                    onClick={() => handleStatusSelect(option.value)}
                    disabled={!selectedJobs.length || !approveAndProcessAccounting}
                    showDisabledReason={!approveAndProcessAccounting}
                    disabledReason={InfoMesssages.noPermissionToApproveAndProcessAccounting}
                    className="h-fit p-1 text-sbase font-semibold text-secondary">
                    Mark as {option.label}
                  </CustomButton>
                ))}
                <If condition={payRunStatus !== PayRunStatus.COMPLETE}>
                  <CustomButton
                    key="move-to-open-pay-run"
                    id="move-to-open-pay-run"
                    type={ButtonType.GHOST}
                    onClick={() => handleMoveToOpenPayRun()}
                    disabled={!selectedJobs.length || !approveAndProcessAccounting}
                    showDisabledReason={!approveAndProcessAccounting}
                    disabledReason={InfoMesssages.noPermissionToApproveAndProcessAccounting}
                    className="h-fit p-1 text-sbase font-semibold text-secondary">
                    Move to an open pay run
                  </CustomButton>
                </If>
              </div>
            </div>
            <TooltipWrapper tooltip="Clear selection" position="top">
              <div id="clear-selection" className="cursor-pointer p-1" onClick={onClearSelection}>
                <NavCloseIcon className="h-[10px] w-[10px]" />
              </div>
            </TooltipWrapper>
          </div>
        ) : (
          <div className="flex w-full items-center justify-end gap-4">
            <CustomButton
              id="export-table-button"
              aria-label="Export table"
              StartIcon={ExportIcon}
              startIconStyle="path-fill-current text-primaryText"
              type={ButtonType.SECONDARY}
              className={`
             h-8 border border-gray-1 bg-transparent py-1.5 px-3 text-sbase 
             font-semibold text-primaryText
           `}
              onClick={onExportClick}>
              <span className="text-sbase font-medium">Export</span>
            </CustomButton>

            <CustomButton
              id="edit-table-button"
              aria-label="Edit table"
              StartIcon={EditTableIcon}
              startIconStyle="path-fill-current text-primaryText"
              type={ButtonType.SECONDARY}
              className="h-8 border border-gray-1 bg-transparent py-2 px-3 text-sbase font-semibold
                        text-primaryText focus:outline-none focus:ring-2 focus:ring-primary"
              onClick={onEditTableClick}>
              <span className="text-sbase font-medium">Edit table</span>
            </CustomButton>
          </div>
        )}
      </div>
      <MoveJobToPayRunModal
        isOpen={isMoveJobToPayRunModalOpen}
        onClose={() => setIsMoveJobToPayRunModalOpen(false)}
        handleMove={handleMove}
        currentPayRunId={payRunId}
        isMoveJobLoading={isMoveBulkJobToPayRunLoading}
      />
    </>
  );
};

export default JobReviewTableActions;
